import { generateUUID } from './generate-uuid';

/** 与文档一致：设备元数据库 */
const IDB_NAME = 'h5r_device';
const IDB_STORE = 'meta';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

function readFromLocalStorage(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeToLocalStorage(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Safari 隐私模式等
    }
}

function readFromCookie(key: string): string | null {
    if (typeof document === 'undefined') {
        return null;
    }
    const prefix = `${key}=`;
    const hit = document.cookie.split(';').find(part => part.trimStart().startsWith(prefix));
    if (!hit) {
        return null;
    }
    const raw = hit.trimStart().slice(prefix.length);
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
}

function writeToCookie(key: string, value: string): void {
    if (typeof document === 'undefined') {
        return;
    }
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    document.cookie = `${key}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax${
        secure ? '; Secure' : ''
    }`;
}

function removeFromLocalStorage(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // Safari 隐私模式等
    }
}

function removeFromCookie(key: string): void {
    if (typeof document === 'undefined') {
        return;
    }
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    document.cookie = `${key}=; max-age=0; path=/; SameSite=Lax${secure ? '; Secure' : ''}`;
}

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(IDB_NAME, 1);
        request.onupgradeneeded = () => {
            if (!request.result.objectStoreNames.contains(IDB_STORE)) {
                request.result.createObjectStore(IDB_STORE);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

async function readFromIndexedDB(key: string): Promise<string | null> {
    try {
        const db = await openDB();
        return await new Promise<string | null>(resolve => {
            const tx = db.transaction(IDB_STORE, 'readonly');
            const store = tx.objectStore(IDB_STORE);
            const req = store.get(key);
            req.onsuccess = () => resolve((req.result as string | undefined) ?? null);
            req.onerror = () => resolve(null);
        });
    } catch {
        return null;
    }
}

async function writeToIndexedDB(key: string, value: string): Promise<void> {
    try {
        const db = await openDB();
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(value, key);
    } catch {
        // IndexedDB 不可用
    }
}

async function deleteFromIndexedDB(key: string): Promise<void> {
    try {
        const db = await openDB();
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).delete(key);
    } catch {
        // IndexedDB 不可用
    }
}

function syncToAllStorages(key: string, value: string): void {
    writeToLocalStorage(key, value);
    writeToCookie(key, value);
    writeToIndexedDB(key, value);
}

const memoryCache = new Map<string, string>();

/** 写入服务端下发的值（不生成 UUID），并同步 L1/L2/L3 + 内存 */
export function persistTripleValue(key: string, value: string): void {
    memoryCache.set(key, value);
    syncToAllStorages(key, value);
}

/** 从内存 + L1/L2/L3 移除指定 key（IDB 异步删除，与 persistTripleValue 一致不阻塞） */
export function removeTripleValue(key: string): void {
    memoryCache.delete(key);
    removeFromLocalStorage(key);
    removeFromCookie(key);
    deleteFromIndexedDB(key);
}

/**
 * 仅读取已持久化的值（不生成）；命中后回填三层与内存。
 * 用于服务端 device_id 等。
 */
export async function readStoredTriple(key: string): Promise<string | null> {
    const cached = memoryCache.get(key);
    if (cached) {
        return cached;
    }

    const fromLS = readFromLocalStorage(key);
    const fromCookie = readFromCookie(key);
    const fromFast = fromLS || fromCookie;

    if (fromFast) {
        memoryCache.set(key, fromFast);
        syncToAllStorages(key, fromFast);
        return fromFast;
    }

    const fromIDB = await readFromIndexedDB(key);

    if (fromIDB) {
        memoryCache.set(key, fromIDB);
        syncToAllStorages(key, fromIDB);
        return fromIDB;
    }

    return null;
}

/**
 * 按「游客登录」文档：L1 localStorage → L2 Cookie → L3 IndexedDB。
 * L1/L2 同步读取，任一有值即返回（不再等 IDB）；仅二者皆空时才读 IndexedDB。
 * 命中后回填三层；皆空则生成 UUID v4 并写入三层。
 */
export async function getOrCreateTriple(key: string): Promise<string> {
    const cached = memoryCache.get(key);
    if (cached) {
        return cached;
    }

    const fromLS = readFromLocalStorage(key);
    const fromCookie = readFromCookie(key);
    const fromFast = fromLS || fromCookie;

    if (fromFast) {
        memoryCache.set(key, fromFast);
        syncToAllStorages(key, fromFast);
        return fromFast;
    }

    const fromIDB = await readFromIndexedDB(key);

    if (fromIDB) {
        memoryCache.set(key, fromIDB);
        syncToAllStorages(key, fromIDB);
        return fromIDB;
    }

    const created = generateUUID();
    memoryCache.set(key, created);
    syncToAllStorages(key, created);
    return created;
}
